import { FEATURES } from 'constants/FeatureConstants';
import { PARTS, SALES, TECHNICIAN } from 'constants/ModeConstants';
import { createDictionary } from 'utils/ObjectUtils';

export const DASHBOARD_CUSTOMIZATION_SOURCE = 'dashboard-customization';

export const BUFFERED_DELAY_MS = 3000;

export const DASHBOARD_TYPES = {
  AFTER_SALES: 'AFTER_SALES',
  AFTER_SALES_CHATBOT: 'AFTER_SALES_CHATBOT',
  AUDITS: 'AUDITS',
  PARTS: 'PARTS',
  SALES: 'SALES',
  SELF_SERVICE: 'SELF_SERVICE',
};

export const DASHBOARD_FIELDS = {
  // Common fields
  APPOINTMENT_SCHEDULE: 'APPOINTMENT_SCHEDULE',
  AUTHOR: 'AUTHOR',
  ASSIGNEE: 'ASSIGNEE',
  BRAND: 'BRAND',
  TAGS: 'TAGS',
  CUSTOMER_KEY: 'CUSTOMER_KEY',
  CUSTOMER_MESSAGE: 'CUSTOMER_MESSAGE',
  CUSTOMER_NAME: 'CUSTOMER_NAME',
  END_DATE: 'END_DATE',
  EXTRAS: 'EXTRAS',
  FINAL_INVOICE_STATUS: 'FINAL_INVOICE_STATUS',
  MODEL: 'MODEL',
  MODULE: 'MODULE',
  PROACTIVE_WORKS: 'PROACTIVE_WORKS',
  REGISTRATION: 'REGISTRATION',
  SELF_SERVICE_STATUS: 'SELF_SERVICE_STATUS',
  SERVICE_ADVISOR: 'SERVICE_ADVISOR',
  START_DATE: 'START_DATE',
  STATUS: 'STATUS',
  STEP: 'STEP',
  TEMPLATE_TITLE: 'TEMPLATE_TITLE',
  VALIDITY_DATE: 'VALIDITY_DATE',
  VIN: 'VIN',
  WORKS_SUMMARY: 'WORKS_SUMMARY',

  // Icon fields
  ICON_PHOTO: 'ICON_PHOTO',
  ICON_VIDEO: 'ICON_VIDEO',
  ICON_CHECKLIST: 'ICON_CHECKLIST',
  ICON_INTERNAL_MESSAGE: 'ICON_INTERNAL_MESSAGE',
  ICON_PAYMENT: 'ICON_PAYMENT',
  ICON_CUSTOMER_WAITING: 'ICON_CUSTOMER_WAITING',

  // Feature-specific fields
  KIOSK_CUSTOMER_PREFERENCE: 'KIOSK_CUSTOMER_PREFERENCE',
  MEDIA: 'MEDIA',
  MOBILITY: 'MOBILITY',

  // Unexisting field. Use to dissociate mobility legend (MOBILITY_LEGEND) and mobility icons (MOBILITY) in the kanban card
  MOBILITY_LEGEND: 'MOBILITY_LEGEND',
};

export const DEFAULT_HIDDEN_FIELDS = createDictionary({
  [DASHBOARD_TYPES.AFTER_SALES]: [DASHBOARD_FIELDS.END_DATE, DASHBOARD_FIELDS.CUSTOMER_NAME, DASHBOARD_FIELDS.VIN],
  [DASHBOARD_TYPES.AFTER_SALES_CHATBOT]: [DASHBOARD_FIELDS.END_DATE, DASHBOARD_FIELDS.CUSTOMER_NAME, DASHBOARD_FIELDS.VIN, DASHBOARD_FIELDS.VALIDITY_DATE],
  [DASHBOARD_TYPES.SALES]: [DASHBOARD_FIELDS.CUSTOMER_NAME, DASHBOARD_FIELDS.VIN],
  [DASHBOARD_TYPES.PARTS]: [DASHBOARD_FIELDS.CUSTOMER_NAME, DASHBOARD_FIELDS.VIN, DASHBOARD_FIELDS.VALIDITY_DATE],
  [DASHBOARD_TYPES.SELF_SERVICE]: [
    DASHBOARD_FIELDS.BRAND,
    DASHBOARD_FIELDS.MODEL,
    DASHBOARD_FIELDS.VIN,
    DASHBOARD_FIELDS.ASSIGNEE,
    DASHBOARD_FIELDS.SERVICE_ADVISOR,
    DASHBOARD_FIELDS.TAGS,
  ],
  [DASHBOARD_TYPES.AUDITS]: [DASHBOARD_FIELDS.CUSTOMER_NAME, DASHBOARD_FIELDS.MODEL, DASHBOARD_FIELDS.BRAND, DASHBOARD_FIELDS.REGISTRATION, DASHBOARD_FIELDS.VIN],
}, []);

export const DASHBOARD_ALLOWED_FIELDS = {
  [DASHBOARD_TYPES.AFTER_SALES]: [
    DASHBOARD_FIELDS.MEDIA,
    DASHBOARD_FIELDS.MODULE,
    DASHBOARD_FIELDS.STEP,
    DASHBOARD_FIELDS.STATUS,
    DASHBOARD_FIELDS.CUSTOMER_NAME,
    DASHBOARD_FIELDS.BRAND,
    DASHBOARD_FIELDS.MODEL,
    DASHBOARD_FIELDS.REGISTRATION,
    DASHBOARD_FIELDS.VIN,
    DASHBOARD_FIELDS.START_DATE,
    DASHBOARD_FIELDS.END_DATE,
    DASHBOARD_FIELDS.AUTHOR,
    DASHBOARD_FIELDS.ASSIGNEE,
    DASHBOARD_FIELDS.SERVICE_ADVISOR,
    DASHBOARD_FIELDS.TAGS,
    DASHBOARD_FIELDS.SELF_SERVICE_STATUS,
    DASHBOARD_FIELDS.WORKS_SUMMARY,
    DASHBOARD_FIELDS.ICON_PHOTO,
    DASHBOARD_FIELDS.ICON_VIDEO,
    DASHBOARD_FIELDS.ICON_CHECKLIST,
    DASHBOARD_FIELDS.ICON_INTERNAL_MESSAGE,
    DASHBOARD_FIELDS.ICON_PAYMENT,
    DASHBOARD_FIELDS.ICON_CUSTOMER_WAITING,
  ],
  [DASHBOARD_TYPES.AFTER_SALES_CHATBOT]: [
    DASHBOARD_FIELDS.MODULE,
    DASHBOARD_FIELDS.STEP,
    DASHBOARD_FIELDS.STATUS,
    DASHBOARD_FIELDS.CUSTOMER_NAME,
    DASHBOARD_FIELDS.BRAND,
    DASHBOARD_FIELDS.MODEL,
    DASHBOARD_FIELDS.REGISTRATION,
    DASHBOARD_FIELDS.VIN,
    DASHBOARD_FIELDS.START_DATE,
    DASHBOARD_FIELDS.END_DATE,
    DASHBOARD_FIELDS.AUTHOR,
    DASHBOARD_FIELDS.ASSIGNEE,
    DASHBOARD_FIELDS.SERVICE_ADVISOR,
    DASHBOARD_FIELDS.TAGS,
    DASHBOARD_FIELDS.WORKS_SUMMARY,
    DASHBOARD_FIELDS.ICON_PHOTO,
    DASHBOARD_FIELDS.ICON_VIDEO,
    DASHBOARD_FIELDS.ICON_CHECKLIST,
    DASHBOARD_FIELDS.ICON_INTERNAL_MESSAGE,
    DASHBOARD_FIELDS.ICON_PAYMENT,
    DASHBOARD_FIELDS.ICON_CUSTOMER_WAITING,
  ],
  [DASHBOARD_TYPES.SALES]: [
    DASHBOARD_FIELDS.MEDIA,
    DASHBOARD_FIELDS.MODULE,
    DASHBOARD_FIELDS.STEP,
    DASHBOARD_FIELDS.STATUS,
    DASHBOARD_FIELDS.CUSTOMER_NAME,
    DASHBOARD_FIELDS.BRAND,
    DASHBOARD_FIELDS.MODEL,
    DASHBOARD_FIELDS.REGISTRATION,
    DASHBOARD_FIELDS.VIN,
    DASHBOARD_FIELDS.START_DATE,
    DASHBOARD_FIELDS.AUTHOR,
    DASHBOARD_FIELDS.ASSIGNEE,
    DASHBOARD_FIELDS.VALIDITY_DATE,
    DASHBOARD_FIELDS.TAGS,
    DASHBOARD_FIELDS.WORKS_SUMMARY,
    DASHBOARD_FIELDS.ICON_PHOTO,
    DASHBOARD_FIELDS.ICON_VIDEO,
    DASHBOARD_FIELDS.ICON_CHECKLIST,
    DASHBOARD_FIELDS.ICON_INTERNAL_MESSAGE,
    DASHBOARD_FIELDS.ICON_PAYMENT,
  ],
  [DASHBOARD_TYPES.PARTS]: [
    DASHBOARD_FIELDS.MEDIA,
    DASHBOARD_FIELDS.MODULE,
    DASHBOARD_FIELDS.STEP,
    DASHBOARD_FIELDS.STATUS,
    DASHBOARD_FIELDS.CUSTOMER_NAME,
    DASHBOARD_FIELDS.BRAND,
    DASHBOARD_FIELDS.MODEL,
    DASHBOARD_FIELDS.REGISTRATION,
    DASHBOARD_FIELDS.VIN,
    DASHBOARD_FIELDS.START_DATE,
    DASHBOARD_FIELDS.AUTHOR,
    DASHBOARD_FIELDS.ASSIGNEE,
    DASHBOARD_FIELDS.VALIDITY_DATE,
    DASHBOARD_FIELDS.TAGS,
    DASHBOARD_FIELDS.WORKS_SUMMARY,
    DASHBOARD_FIELDS.ICON_PHOTO,
    DASHBOARD_FIELDS.ICON_VIDEO,
    DASHBOARD_FIELDS.ICON_CHECKLIST,
    DASHBOARD_FIELDS.ICON_INTERNAL_MESSAGE,
    DASHBOARD_FIELDS.ICON_PAYMENT,
  ],
  [DASHBOARD_TYPES.SELF_SERVICE]: [
    DASHBOARD_FIELDS.MODULE,
    DASHBOARD_FIELDS.STATUS,
    DASHBOARD_FIELDS.REGISTRATION,
    DASHBOARD_FIELDS.CUSTOMER_NAME,
    DASHBOARD_FIELDS.BRAND,
    DASHBOARD_FIELDS.MODEL,
    DASHBOARD_FIELDS.VIN,
    DASHBOARD_FIELDS.ASSIGNEE,
    DASHBOARD_FIELDS.SERVICE_ADVISOR,
    DASHBOARD_FIELDS.TAGS,
    DASHBOARD_FIELDS.APPOINTMENT_SCHEDULE,
    DASHBOARD_FIELDS.KIOSK_CUSTOMER_PREFERENCE,
    DASHBOARD_FIELDS.PROACTIVE_WORKS,
    DASHBOARD_FIELDS.EXTRAS,
    DASHBOARD_FIELDS.CUSTOMER_MESSAGE,
    DASHBOARD_FIELDS.STEP,
    DASHBOARD_FIELDS.MOBILITY,
    DASHBOARD_FIELDS.FINAL_INVOICE_STATUS,
    DASHBOARD_FIELDS.CUSTOMER_KEY,
  ],
  [DASHBOARD_TYPES.AUDITS]: [
    DASHBOARD_FIELDS.STATUS,
    DASHBOARD_FIELDS.CUSTOMER_NAME,
    DASHBOARD_FIELDS.BRAND,
    DASHBOARD_FIELDS.MODEL,
    DASHBOARD_FIELDS.ASSIGNEE,
    DASHBOARD_FIELDS.REGISTRATION,
    DASHBOARD_FIELDS.VIN,
    DASHBOARD_FIELDS.TEMPLATE_TITLE,
    DASHBOARD_FIELDS.START_DATE,
    DASHBOARD_FIELDS.AUTHOR,
  ],
};

export const DASHBOARD_FIELDS_LABELS = {
  [DASHBOARD_FIELDS.MEDIA]: createDictionary({}, { id: 'media', defaultMessage: 'Media' }),
  [DASHBOARD_FIELDS.MODULE]: createDictionary({}, { id: 'module', defaultMessage: 'Module' }),
  [DASHBOARD_FIELDS.STEP]: createDictionary({}, { id: 'step', defaultMessage: 'Step' }),
  [DASHBOARD_FIELDS.STATUS]: createDictionary({}, { id: 'status', defaultMessage: 'Status' }),
  [DASHBOARD_FIELDS.CUSTOMER_NAME]: createDictionary({}, { id: 'payments.customer_name', defaultMessage: 'Customer name' }),
  [DASHBOARD_FIELDS.BRAND]: createDictionary({}, { id: 'brandName', defaultMessage: 'Brand name' }),
  [DASHBOARD_FIELDS.MODEL]: createDictionary({}, { id: 'dashboard.customization.fields.vehicleModel', defaultMessage: 'Vehicle model' }),
  [DASHBOARD_FIELDS.REGISTRATION]: createDictionary({}, { id: 'page.fiche.form.vehicle_information.plate_number', defaultMessage: 'Plate number' }),
  [DASHBOARD_FIELDS.VIN]: createDictionary({}, { id: 'page.fiche.form.vehicle_information.chassis', defaultMessage: 'Chassis number' }),
  [DASHBOARD_FIELDS.END_DATE]: createDictionary({}, { id: 'endDate', defaultMessage: 'End date' }),
  [DASHBOARD_FIELDS.AUTHOR]: createDictionary({}, { id: 'page.stats.contributorAuthor', defaultMessage: 'Author' }),
  [DASHBOARD_FIELDS.ASSIGNEE]: createDictionary({}, { id: 'page.leads.dashboard.table.assignee', defaultMessage: 'Assignee' }),
  [DASHBOARD_FIELDS.SERVICE_ADVISOR]: createDictionary(
    {},
    { id: 'page.steps.details.customerDescription.tags.service_advisor', defaultMessage: 'Service advisor' }
  ),
  [DASHBOARD_FIELDS.VALIDITY_DATE]: createDictionary({}, { id: 'dashboard.customization.fields.validityDate', defaultMessage: 'Validity date' }),
  [DASHBOARD_FIELDS.SELF_SERVICE_STATUS]: createDictionary({}, { id: 'page.home.filters.type.selfServiceStatus', defaultMessage: 'Self-service status' }),
  [DASHBOARD_FIELDS.TAGS]: createDictionary({}, { id: 'page.config.dealer_general.tags.title', defaultMessage: 'Tags' }),
  [DASHBOARD_FIELDS.WORKS_SUMMARY]: createDictionary({}, { id: 'dashboard.customization.fields.worksSummary', defaultMessage: 'Work summary' }),
  [DASHBOARD_FIELDS.APPOINTMENT_SCHEDULE]: createDictionary(
    {},
    { id: 'dashboard.customization.fields.appointmentSchedule', defaultMessage: 'Appointment schedule' }
  ),
  [DASHBOARD_FIELDS.CUSTOMER_KEY]: createDictionary({}, { id: 'dashboard.customization.fields.customerKey', defaultMessage: 'Customer car key status' }),
  [DASHBOARD_FIELDS.CUSTOMER_MESSAGE]: createDictionary({}, { id: 'interventionKanban.card.footer.receivedMessage', defaultMessage: 'Customer message' }),
  [DASHBOARD_FIELDS.EXTRAS]: createDictionary({}, { id: 'checkIn.extras', defaultMessage: 'Extras' }),
  [DASHBOARD_FIELDS.FINAL_INVOICE_STATUS]: createDictionary({}, { id: 'dashboard.customization.fields.finalInvoiceStatus', defaultMessage: 'Invoice status' }),
  [DASHBOARD_FIELDS.PROACTIVE_WORKS]: createDictionary({}, { id: 'checkIn.proactiveWorks', defaultMessage: 'Proactive works' }),
  [DASHBOARD_FIELDS.KIOSK_CUSTOMER_PREFERENCE]: createDictionary(
    {},
    { id: 'dashboard.customization.fields.kioskCustomerPreference', defaultMessage: 'Kiosk Customer preferences' }
  ),
  [DASHBOARD_FIELDS.MOBILITY]: createDictionary({}, { id: 'dashboard.customization.fields.mobility', defaultMessage: 'Mobility status' }),
  [DASHBOARD_FIELDS.TEMPLATE_TITLE]: createDictionary({}, { id: 'dashboard.customization.fields.auditTemplate', defaultMessage: 'Audit template' }),
  // Icon fields
  [DASHBOARD_FIELDS.ICON_PHOTO]: createDictionary({}, { id: 'cameraDevice.photo', defaultMessage: 'Photo' }),
  [DASHBOARD_FIELDS.ICON_VIDEO]: createDictionary({}, { id: 'cameraDevice.video', defaultMessage: 'Video' }),
  [DASHBOARD_FIELDS.ICON_CHECKLIST]: createDictionary({}, { id: 'dashboard.customization.fields.iconChecklist', defaultMessage: 'Imported audit' }),
  [DASHBOARD_FIELDS.ICON_INTERNAL_MESSAGE]: createDictionary({}, { id: 'intervention_card.alt_internal_message', defaultMessage: 'Internal message' }),
  [DASHBOARD_FIELDS.ICON_PAYMENT]: createDictionary({}, { id: 'intervention_card.alt_paid_item', defaultMessage: 'Paid elements' }),
  [DASHBOARD_FIELDS.ICON_CUSTOMER_WAITING]: createDictionary({}, { id: 'intervention_card.alt_customer_waiting', defaultMessage: 'Customer waiting' }),
  // Specific translations
  [DASHBOARD_FIELDS.START_DATE]: createDictionary(
    {
      [DASHBOARD_TYPES.PARTS]: { id: 'page.stats.creationDate', defaultMessage: 'Creation date' },
      [DASHBOARD_TYPES.SALES]: { id: 'page.stats.creationDate', defaultMessage: 'Creation date' },
    },
    { id: 'startDate', defaultMessage: 'Start date' }
  ),
};

/** @type {Partial<Record<import('modules/global/typing/enums/modes.enum').ModeEnum, import('modules/users/types/dashboard.enum').DashboardEnum>>} */
export const MODE_TO_DASHBOARD_TYPE = {
  [TECHNICIAN]: DASHBOARD_TYPES.AFTER_SALES,
  [SALES]: DASHBOARD_TYPES.SALES,
  [PARTS]: DASHBOARD_TYPES.PARTS,
};

export const ICON_FIELDS = [
  DASHBOARD_FIELDS.ICON_PHOTO,
  DASHBOARD_FIELDS.ICON_VIDEO,
  DASHBOARD_FIELDS.ICON_PAYMENT,
  DASHBOARD_FIELDS.ICON_CHECKLIST,
  DASHBOARD_FIELDS.ICON_INTERNAL_MESSAGE,
  DASHBOARD_FIELDS.ICON_CUSTOMER_WAITING,
];

export const DASHBOARD_TYPE_TO_TAG_FEATURE = {
  [DASHBOARD_TYPES.AFTER_SALES]: FEATURES.TAGS_AFTERSALES,
  [DASHBOARD_TYPES.SELF_SERVICE]: FEATURES.TAGS_AFTERSALES,
  [DASHBOARD_TYPES.SALES]: FEATURES.TAGS_SALES,
  [DASHBOARD_TYPES.PARTS]: FEATURES.TAGS_PARTS,
  [DASHBOARD_TYPES.AFTER_SALES_CHATBOT]: FEATURES.TAGS_AFTERSALES,
};
